'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { useCheckoutPagamento } from '@/shared/hooks/useCheckoutPagamento';
import { useToast } from '@/shared/providers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { AcaoAutorizacaoAr } from '@/types/arbitral/envioArTypes';
import { autorizarConvenioAr, desfazerRecusaAr, devolverEnderecoAr, fetchAutorizacoesAr, recusarEnvioAr, verificarPagamentoAr } from '../store/autorizacaoAr/actions';
import { type CodigoErroAutorizacaoAr, DETALHE_POR_CODIGO, type FalhaAutorizacaoAr, TITULO_POR_CODIGO } from '../store/autorizacaoAr/erros';
import { selectAcaoEmVooAr, selectEnviosAr, selectErroLeituraAr, selectResumoAr } from '../store/autorizacaoAr/selectors';
import { limparAutorizacoesAr } from '../store/autorizacaoAr/slice';
import type { EnvioParaDecidir } from '../store/autorizacaoAr/tipos';
import { esperaDesfechoDoServidor } from '../utils/autorizacaoArEspera';
function falhaDe(payload: unknown): FalhaAutorizacaoAr | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const candidata = payload as Partial<FalhaAutorizacaoAr>;
  return typeof candidata.mensagem === 'string' ? candidata as FalhaAutorizacaoAr : null;
}

/**
 * Falhas da verificação em que continuar esperando não muda nada: não há cobrança a
 * pagar, ou a solicitação já não aceita pagamento. As demais (gateway fora, verificação
 * em curso, rate limit) mantêm a espera — o webhook ainda pode confirmar o que o
 * requerente pagou, e o polling tem tetos próprios de relógio e de erro.
 */
const FALHAS_QUE_ENCERRAM_A_ESPERA: readonly CodigoErroAutorizacaoAr[] = ['COBRANCA_NAO_EMITIDA', 'SOLICITACAO_JA_DECIDIDA', 'SOLICITACAO_SUPERADA', 'ACAO_INDISPONIVEL_NA_MODALIDADE', 'PROCESSO_FORA_DE_CITACAO'];

/**
 * A porta do requerente sobre os envios de AR do processo.
 *
 * **Uma linha por REQUERIDO**, não uma por processo: cada envio tem endereço, valor e
 * prazo próprios, e um card só faria o requerente autorizar sem saber qual dos dois
 * está pagando. A ordem vem do servidor, por urgência.
 *
 * O hook é fino de propósito — despacha thunks e lê selectors. O estado dos envios é de
 * aplicação, então mora no slice; o que fica aqui é só o que pertence a ESTA aba:
 * o checkout aberto e o popup bloqueado, que um F5 deve esquecer.
 */
export function useAutorizacaoAr(processoArbitralId) {
  const $ = _c(97);
  const dispatch = useAppDispatch();
  const toast = useToast();
  const linhasDoServidor = useAppSelector(selectEnviosAr) ?? [];
  const resumo = useAppSelector(selectResumoAr);
  const acaoEmVoo = useAppSelector(selectAcaoEmVooAr) ?? {};
  const erroLeitura = useAppSelector(selectErroLeituraAr) ?? null;
  const [solicitacaoEmCheckout, setSolicitacaoEmCheckout] = useState(null);
  const verificacaoEmVooRef = useRef(null);
  let t0;
  if ($[0] !== dispatch || $[1] !== processoArbitralId) {
    t0 = () => {
      if (processoArbitralId) {
        dispatch(fetchAutorizacoesAr(processoArbitralId));
      }
    };
    $[0] = dispatch;
    $[1] = processoArbitralId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== linhasDoServidor || $[4] !== solicitacaoEmCheckout) {
    t1 = solicitacaoEmCheckout !== null || linhasDoServidor.some(_temp);
    $[3] = linhasDoServidor;
    $[4] = solicitacaoEmCheckout;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== toast) {
    t2 = motivo => motivo === "TEMPO" ? toast.showError("Ainda n\xE3o identificamos o pagamento", "Se voc\xEA j\xE1 pagou, o status atualiza sozinho assim que o Asaas confirmar.") : toast.showError("N\xE3o conseguimos verificar o pagamento", "A consulta falhou v\xE1rias vezes seguidas. Se voc\xEA j\xE1 pagou, nada foi perdido \u2014 recarregue a p\xE1gina em alguns minutos.");
    $[6] = toast;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== erroLeitura || $[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t3 = {
      consultar: t0,
      erroLeitura,
      pollingPassivo: t1,
      aoDesistir: t2
    };
    $[8] = erroLeitura;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const {
    fase,
    popupBloqueado,
    abrirCheckout,
    verificarPagamento,
    fecharCheckout,
    limparPopupBloqueado
  } = useCheckoutPagamento(t3);
  let t4;
  let t5;
  if ($[13] !== dispatch || $[14] !== processoArbitralId) {
    t4 = () => {
      if (!processoArbitralId) {
        dispatch(limparAutorizacoesAr());
        return;
      }
      dispatch(fetchAutorizacoesAr(processoArbitralId));
      return () => {
        dispatch(limparAutorizacoesAr());
      };
    };
    t5 = [dispatch, processoArbitralId];
    $[13] = dispatch;
    $[14] = processoArbitralId;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[17] !== fase) {
    t6 = () => {
      if (fase === "FECHADO") {
        setSolicitacaoEmCheckout(null);
      }
    };
    t7 = [fase];
    $[17] = fase;
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  useEffect(t6, t7);
  let t8;
  if ($[20] !== linhasDoServidor || $[21] !== solicitacaoEmCheckout) {
    t8 = linhasDoServidor.find(linha_0 => linha_0.envio.solicitacaoId === solicitacaoEmCheckout)?.envio.estado ?? null;
    $[20] = linhasDoServidor;
    $[21] = solicitacaoEmCheckout;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const estadoDoEnvioEmCheckout = t8;
  let t10;
  let t9;
  if ($[23] !== estadoDoEnvioEmCheckout || $[24] !== fase || $[25] !== fecharCheckout) {
    t9 = () => {
      if (fase === "FECHADO" || estadoDoEnvioEmCheckout === null) {
        return;
      }
      if (estadoDoEnvioEmCheckout === "AGUARDANDO_AUTORIZACAO") {
        return;
      }
      fecharCheckout();
    };
    t10 = [estadoDoEnvioEmCheckout, fase, fecharCheckout];
    $[23] = estadoDoEnvioEmCheckout;
    $[24] = fase;
    $[25] = fecharCheckout;
    $[26] = t10;
    $[27] = t9;
  } else {
    t10 = $[26];
    t9 = $[27];
  }
  useEffect(t9, t10);
  let t11;
  if ($[28] !== linhasDoServidor) {
    t11 = solicitacaoId => linhasDoServidor.find(linha_1 => linha_1.envio.solicitacaoId === solicitacaoId)?.envio ?? null;
    $[28] = linhasDoServidor;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const envioDe = t11;
  let t12;
  if ($[30] !== envioDe) {
    t12 = (solicitacaoId_0, acao) => envioDe(solicitacaoId_0)?.acoes.includes(acao) ?? false;
    $[30] = envioDe;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const podeAgir = t12;
  let t13;
  if ($[32] !== dispatch || $[33] !== processoArbitralId || $[34] !== toast) {
    t13 = async (acao_0, titulo, detalhe) => {
      const resultado = await dispatch(acao_0);
      if (resultado.meta.requestStatus === "fulfilled") {
        toast.showSuccess(titulo, detalhe);
        return;
      }
      const falha = falhaDe(resultado.payload);
      const codigo = falha?.codigo ?? null;
      toast.showError(codigo ? TITULO_POR_CODIGO[codigo] : "N\xE3o foi poss\xEDvel registrar sua decis\xE3o", codigo ? DETALHE_POR_CODIGO[codigo] : falha?.mensagem ?? "Tente novamente em instantes.");
      if (codigo === "SOLICITACAO_JA_DECIDIDA" && falha?.estadoAtual) {
        return;
      }
      if (processoArbitralId) {
        dispatch(fetchAutorizacoesAr(processoArbitralId));
      }
    };
    $[32] = dispatch;
    $[33] = processoArbitralId;
    $[34] = toast;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const despacharDecisao = t13;
  let t14;
  if ($[36] !== acaoEmVoo || $[37] !== despacharDecisao || $[38] !== podeAgir || $[39] !== processoArbitralId) {
    t14 = solicitacaoId_1 => {
      if (!processoArbitralId || !podeAgir(solicitacaoId_1, "AUTORIZAR")) {
        return;
      }
      if (acaoEmVoo[solicitacaoId_1]) {
        return;
      }
      despacharDecisao(autorizarConvenioAr({
        processoArbitralId,
        solicitacaoId: solicitacaoId_1
      }), "Envio autorizado", "A carta entra na fila de postagem e o valor vai para a fatura do seu ciclo.");
    };
    $[36] = acaoEmVoo;
    $[37] = despacharDecisao;
    $[38] = podeAgir;
    $[39] = processoArbitralId;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const autorizarConvenio = t14;
  let t15;
  if ($[41] !== abrirCheckout || $[42] !== envioDe || $[43] !== podeAgir || $[44] !== toast) {
    t15 = solicitacaoId_2 => {
      if (!podeAgir(solicitacaoId_2, "PAGAR")) {
        return;
      }
      const resultado_0 = abrirCheckout(envioDe(solicitacaoId_2)?.linkPagamento);
      if (resultado_0 === "URL_INVALIDA") {
        toast.showError("Pagamento indispon\xEDvel", "O link de pagamento ainda n\xE3o foi gerado. Tente novamente em instantes.");
        return;
      }
      if (resultado_0 === "POPUP_BLOQUEADO") {
        return;
      }
      setSolicitacaoEmCheckout(solicitacaoId_2);
    };
    $[41] = abrirCheckout;
    $[42] = envioDe;
    $[43] = podeAgir;
    $[44] = toast;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const pagarEnvio = t15;
  let t16;
  if ($[46] !== dispatch || $[47] !== fecharCheckout || $[48] !== processoArbitralId || $[49] !== toast) {
    t16 = falha_0 => {
      const codigo_0 = falha_0?.codigo ?? null;
      toast.showError(codigo_0 ? TITULO_POR_CODIGO[codigo_0] : "N\xE3o conseguimos verificar o pagamento", codigo_0 ? DETALHE_POR_CODIGO[codigo_0] : "Se voc\xEA j\xE1 pagou, nada foi perdido \u2014 a tela continua conferindo sozinha.");
      if (!codigo_0 || !FALHAS_QUE_ENCERRAM_A_ESPERA.includes(codigo_0)) {
        return;
      }
      fecharCheckout();
      if (codigo_0 === "SOLICITACAO_JA_DECIDIDA" && falha_0?.estadoAtual) {
        return;
      }
      if (processoArbitralId) {
        dispatch(fetchAutorizacoesAr(processoArbitralId));
      }
    };
    $[46] = dispatch;
    $[47] = fecharCheckout;
    $[48] = processoArbitralId;
    $[49] = toast;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  const avisarFalhaDaVerificacao = t16;
  let t17;
  if ($[51] !== avisarFalhaDaVerificacao || $[52] !== dispatch || $[53] !== podeAgir || $[54] !== processoArbitralId || $[55] !== verificarPagamento) {
    t17 = solicitacaoId_3 => {
      if (!processoArbitralId || !podeAgir(solicitacaoId_3, "VERIFICAR_PAGAMENTO")) {
        return;
      }
      if (verificacaoEmVooRef.current !== null) {
        return;
      }
      verificacaoEmVooRef.current = solicitacaoId_3;
      setSolicitacaoEmCheckout(solicitacaoId_3);
      verificarPagamento();
      dispatch(verificarPagamentoAr({
        processoArbitralId,
        solicitacaoId: solicitacaoId_3
      })).then(resultado_1 => {
        verificacaoEmVooRef.current = null;
        if (resultado_1.meta.requestStatus === "fulfilled") {
          return;
        }
        avisarFalhaDaVerificacao(falhaDe(resultado_1.payload));
      });
    };
    $[51] = avisarFalhaDaVerificacao;
    $[52] = dispatch;
    $[53] = podeAgir;
    $[54] = processoArbitralId;
    $[55] = verificarPagamento;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  const confirmarPagamento = t17;
  let t18;
  if ($[57] !== fecharCheckout) {
    t18 = () => fecharCheckout();
    $[57] = fecharCheckout;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  const voltarParaCobranca = t18;
  let t19;
  if ($[59] !== acaoEmVoo || $[60] !== despacharDecisao || $[61] !== fecharCheckout || $[62] !== limparPopupBloqueado || $[63] !== podeAgir || $[64] !== processoArbitralId) {
    t19 = (solicitacaoId_4, observacao) => {
      if (!processoArbitralId || !observacao.trim()) {
        return;
      }
      if (!podeAgir(solicitacaoId_4, "DEVOLVER_ENDERECO") || acaoEmVoo[solicitacaoId_4]) {
        return;
      }
      fecharCheckout();
      limparPopupBloqueado();
      despacharDecisao(devolverEnderecoAr({
        processoArbitralId,
        solicitacaoId: solicitacaoId_4,
        observacao: observacao.trim()
      }), "Endere\xE7o devolvido para corre\xE7\xE3o", "Nada foi cobrado. A secretaria vai revisar e enviar uma nova solicita\xE7\xE3o.");
    };
    $[59] = acaoEmVoo;
    $[60] = despacharDecisao;
    $[61] = fecharCheckout;
    $[62] = limparPopupBloqueado;
    $[63] = podeAgir;
    $[64] = processoArbitralId;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  const devolverEndereco = t19;
  let t20;
  if ($[66] !== acaoEmVoo || $[67] !== despacharDecisao || $[68] !== fecharCheckout || $[69] !== limparPopupBloqueado || $[70] !== podeAgir || $[71] !== processoArbitralId) {
    t20 = (solicitacaoId_5, motivo_0) => {
      if (!processoArbitralId || !podeAgir(solicitacaoId_5, "RECUSAR")) {
        return;
      }
      if (acaoEmVoo[solicitacaoId_5]) {
        return;
      }
      fecharCheckout();
      limparPopupBloqueado();
      despacharDecisao(recusarEnvioAr({
        processoArbitralId,
        solicitacaoId: solicitacaoId_5,
        motivo: motivo_0.trim() || null
      }), "Recusa registrada", "A secretaria foi avisada e vai avaliar o pr\xF3ximo passo.");
    };
    $[66] = acaoEmVoo;
    $[67] = despacharDecisao;
    $[68] = fecharCheckout;
    $[69] = limparPopupBloqueado;
    $[70] = podeAgir;
    $[71] = processoArbitralId;
    $[72] = t20;
  } else {
    t20 = $[72];
  }
  const recusarEnvio = t20;
  let t21;
  if ($[73] !== acaoEmVoo || $[74] !== despacharDecisao || $[75] !== podeAgir || $[76] !== processoArbitralId) {
    t21 = solicitacaoId_6 => {
      if (!processoArbitralId || !podeAgir(solicitacaoId_6, "DESFAZER_RECUSA")) {
        return;
      }
      if (acaoEmVoo[solicitacaoId_6]) {
        return;
      }
      despacharDecisao(desfazerRecusaAr({
        processoArbitralId,
        solicitacaoId: solicitacaoId_6
      }), "Recusa desfeita", "A solicita\xE7\xE3o voltou a aguardar a sua decis\xE3o.");
    };
    $[73] = acaoEmVoo;
    $[74] = despacharDecisao;
    $[75] = podeAgir;
    $[76] = processoArbitralId;
    $[77] = t21;
  } else {
    t21 = $[77];
  }
  const desfazerRecusa = t21;
  let t22;
  if ($[78] !== fase || $[79] !== linhasDoServidor || $[80] !== solicitacaoEmCheckout) {
    let t23;
    if ($[82] !== fase || $[83] !== solicitacaoEmCheckout) {
      t23 = linha_2 => {
        if (linha_2.envio.solicitacaoId !== solicitacaoEmCheckout || fase === "FECHADO") {
          return linha_2;
        }
        if (linha_2.envio.estado !== "AGUARDANDO_AUTORIZACAO") {
          return linha_2;
        }
        return {
          ...linha_2,
          envio: {
            ...linha_2.envio,
            estado: fase === "VERIFICANDO" ? "VERIFICANDO" : "PROCESSANDO"
          }
        };
      };
      $[82] = fase;
      $[83] = solicitacaoEmCheckout;
      $[84] = t23;
    } else {
      t23 = $[84];
    }
    t22 = linhasDoServidor.map(t23);
    $[78] = fase;
    $[79] = linhasDoServidor;
    $[80] = solicitacaoEmCheckout;
    $[81] = t22;
  } else {
    t22 = $[81];
  }
  let t23;
  if ($[85] !== acaoEmVoo || $[86] !== autorizarConvenio || $[87] !== confirmarPagamento || $[88] !== desfazerRecusa || $[89] !== devolverEndereco || $[90] !== pagarEnvio || $[91] !== popupBloqueado || $[92] !== recusarEnvio || $[93] !== resumo || $[94] !== t22 || $[95] !== voltarParaCobranca) {
    t23 = {
      envios: t22,
      resumo,
      acaoEmVoo,
      popupBloqueado,
      autorizarConvenio,
      pagarEnvio,
      confirmarPagamento,
      voltarParaCobranca,
      devolverEndereco,
      recusarEnvio,
      desfazerRecusa
    };
    $[85] = acaoEmVoo;
    $[86] = autorizarConvenio;
    $[87] = confirmarPagamento;
    $[88] = desfazerRecusa;
    $[89] = devolverEndereco;
    $[90] = pagarEnvio;
    $[91] = popupBloqueado;
    $[92] = recusarEnvio;
    $[93] = resumo;
    $[94] = t22;
    $[95] = voltarParaCobranca;
    $[96] = t23;
  } else {
    t23 = $[96];
  }
  return t23;
}
function _temp(linha) {
  return esperaDesfechoDoServidor(linha.envio);
}